import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { api, logError } from "@/lib/apiClient";
import { useI18n } from "@/i18n";
import { ActivityCard } from "@/components/ActivityCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";

export default function Activities() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxPrice, setMaxPrice] = useState(100);
  const [sort, setSort] = useState("");

  const category = params.get("category") || "";
  const search = params.get("search") || "";

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch((e) => logError("categories load failed", e)); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (category) q.category = category;
    if (search) q.search = search;
    if (maxPrice < 100) q.max_price = maxPrice;
    if (sort) q.sort = sort;
    api.get("/activities", { params: q })
      .then((r) => setActivities(r.data))
      .catch((e) => logError("activities load failed", e))
      .finally(() => setLoading(false));
  }, [category, search, maxPrice, sort]);

  useEffect(() => { load(); }, [load]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c) next.set("category", c); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
        {category ? categories.find((c) => c.id === category)?.name || t("nav_activities") : t("nav_activities")}
      </h1>
      {search && <p className="text-muted-foreground mt-1">"{search}"</p>}

      {/* Category chips */}
      <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <button data-testid="chip-all" onClick={() => setCategory("")}
          className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${!category ? "bg-primary text-primary-foreground border-primary" : "bg-white border-black/10 hover:bg-secondary"}`}>
          {t("all")}
        </button>
        {categories.map((c) => (
          <button key={c.id} data-testid={`chip-${c.id}`} onClick={() => setCategory(c.id)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${category === c.id ? "bg-primary text-primary-foreground border-primary" : "bg-white border-black/10 hover:bg-secondary"}`}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-6">
          <div className="rounded-2xl bg-white border border-black/5 soft-shadow p-5">
            <div className="flex items-center gap-2 font-semibold font-display mb-4">
              <SlidersHorizontal className="h-4 w-4" /> {t("filters")}
            </div>
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-3">
                <span className="text-muted-foreground">{t("price_range")}</span>
                <span className="font-semibold">€0 – €{maxPrice}{maxPrice >= 100 ? "+" : ""}</span>
              </div>
              <Slider data-testid="price-slider" value={[maxPrice]} min={10} max={100} step={5} onValueChange={(v) => setMaxPrice(v[0])} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">{t("sort")}</label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select"><SelectValue placeholder={t("sort_recommended")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">{t("sort_rating")}</SelectItem>
                  <SelectItem value="price_asc">{t("sort_price_low")}</SelectItem>
                  <SelectItem value="price_desc">{t("sort_price_high")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div>
          {!loading && <p className="text-sm text-muted-foreground mb-4">{activities.length} {t("results")}</p>}
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {[...Array(6)].map((_, i) => <Skeleton key={`sk-${i}`} className="aspect-[4/3] rounded-3xl" />)}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground" data-testid="no-results">No experiences match your filters.</div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="activities-grid">
              {activities.map((a, i) => <ActivityCard key={a.id} activity={a} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
